import { useState, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useCurrentUser } from '@/features/auth/useAuth'
import { Sidebar } from './Sidebar'
import { Navbar } from './Navbar'

interface AppLayoutProps {
  children: ReactNode
  requireRole?: 'user' | 'admin'
}

export function AppLayout({ children, requireRole }: AppLayoutProps) {
  const { data: user, isLoading, isError } = useCurrentUser()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-paper">
        <div className="text-sm text-ink-mute">กำลังโหลด…</div>
      </div>
    )
  }
  if (isError || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  if (requireRole && user.type !== requireRole) {
    return <Navigate to={user.type === 'admin' ? '/admin' : '/'} replace />
  }

  return (
    <div className="min-h-screen flex bg-paper">
      <div className="hidden lg:block">
        <Sidebar user={user} />
      </div>
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setSidebarOpen(false)} />
          <div className="relative">
            <Sidebar user={user} onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <Navbar user={user} onToggleSidebar={() => setSidebarOpen((v) => !v)} />
        <main className="flex-1 p-6 sm:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">{children}</div>
        </main>
      </div>
    </div>
  )
}
